import React from 'react';
import { SEO } from '../components/seo/SEO';
import { Phone, Mail, MapPin, Building2, CreditCard, Copy, CheckCircle2 } from 'lucide-react';
import { getBreadcrumbSchema } from '../lib/jsonLd';

export function ContactPage() {
  const [copiedField, setCopiedField] = React.useState<string | null>(null);
  const [form, setForm] = React.useState({ name: '', email: '', message: '' });
  const [step, setStep] = React.useState<'edit' | 'review' | 'sent'>('edit');
  const ENABLE_CONTACT_FORM = false;

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Strona główna', url: 'https://znajdki.pl/' },
    { name: 'Kontakt', url: 'https://znajdki.pl/kontakt' }
  ]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-16">
      <SEO 
        title="Kontakt - Fundacja Znajdki"
        description="Skontaktuj się z nami! Fundacja Znajdki w Kruszu k.Radzymina. Telefon: 664 821 160, email: znajdki2012@gmail.com. Odwiedź przytulisko lub napisz do nas."
        url="/kontakt"
        image="/images/animals/fundacja-znajdki-zwierzeta-001.webp"
        jsonLd={breadcrumbSchema}
      />

      <div className="text-center mb-12">
        <h1 className="heading-1 mb-4">
          Kontakt
        </h1>
        <p className="body-large">
          Jesteśmy tutaj, aby odpowiedzieć na Twoje pytania
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
        {/* Przytulisko */}
        <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
          {/* Accessibility Info */}
          <div className="mb-8 border-b border-accessible-gray-200 dark:border-accessible-gray-700 pb-8">
            <h3 className="heading-5 mb-4">
              Dostępność
            </h3>
            <ul className="space-y-2 body-medium">
              <li className="flex items-start gap-2">
                <span className="text-accessible-red-600">✓</span>
                Teren schroniska jest płaski, bez wzniesień i schodów
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accessible-red-600">✓</span>
                Możliwe poruszanie się na wózku inwalidzkim
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accessible-red-600">✓</span>
                Duży parking przed schroniskiem
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accessible-red-600">⚠</span>
                Siedziba administracyjna nie jest w pełni dostosowana dla osób niepełnosprawnych
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accessible-red-600">⚠</span>
                Brak toalety przystosowanej dla osób niepełnosprawnych
              </li>
            </ul>
          </div>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-accessible-red-600" />
            </div>
            <h2 className="heading-3">
              Przytulisko
            </h2>
          </div>
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <MapPin className="w-6 h-6 text-accessible-red-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="heading-6 mb-1">
                  Adres
                </h3>
                <a 
                  href="https://maps.app.goo.gl/JFV5T57iDLKpha7P9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="body-medium hover:text-accessible-red-600 no-margin"
                >
                  Fundacja Znajdki<br />
                  Krusze ul. Kryształowa 171<br />
                  05-240 Tłuszcz
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone className="w-6 h-6 text-accessible-red-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="heading-6 mb-1">
                  Telefon
                </h3>
                <div className="flex items-center gap-3">
                  <a 
                    href="tel:+48664821160" 
                    className="body-medium hover:text-accessible-red-600 no-margin"
                  >
                    664 821 160
                  </a>
                  <button
                    onClick={() => copyToClipboard('664821160', 'phone')}
                    className="p-2 hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-700 rounded-lg transition-colors"
                    title="Skopiuj numer telefonu"
                    aria-label="Skopiuj numer telefonu"
                  >
                    {copiedField === 'phone' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Mail className="w-6 h-6 text-accessible-red-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="heading-6 mb-1">
                  Email
                </h3>
                <div className="flex items-center gap-3">
                  <a 
                    href="mailto:znajdki2012@gmail.com" 
                    className="body-medium hover:text-accessible-red-600 no-margin"
                  >
                    znajdki2012@gmail.com
                  </a>
                  <button
                    onClick={() => copyToClipboard('znajdki2012@gmail.com', 'email')}
                    className="p-2 hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-700 rounded-lg transition-colors"
                    title="Skopiuj adres email"
                    aria-label="Skopiuj adres email"
                  >
                    {copiedField === 'email' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dane fundacji */}
        <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-accessible-red-600" />
            </div>
            <h2 className="heading-3">
              Dane fundacji
            </h2>
          </div>
          <div className="space-y-6">
            <div>
              <h3 className="heading-6 mb-2">
                Nazwa Fundacji
              </h3>
              <div className="flex items-center justify-between bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-white hc-on-white rounded p-3">
                <p className="body-medium no-margin">
                  Fundacja ZNAJDKI
                </p>
                <button
                  onClick={() => copyToClipboard('Fundacja ZNAJDKI', 'name')}
                  className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                  title="Skopiuj nazwę"
                  aria-label="Skopiuj nazwę"
                >
                  {copiedField === 'name' ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />}
                </button>
              </div>
            </div>
            <div>
              <h3 className="heading-6 mb-2">
                Adres
              </h3>
              <div className="flex items-center justify-between bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-white hc-on-white rounded p-3">
                <p className="body-medium no-margin">
                  ul. Kryształowa 171, 05-240 Krusze
                </p>
                <button
                  onClick={() => copyToClipboard('ul. Kryształowa 171, 05-240 Krusze', 'address')}
                  className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                  title="Skopiuj adres"
                  aria-label="Skopiuj adres"
                >
                  {copiedField === 'address' ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />}
                </button>
              </div>
            </div>
            <div>
              <h3 className="heading-6 mb-2">
                Identyfikatory
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-white hc-on-white rounded p-3">
                  <p className="body-medium no-margin">
                    <span className="font-semibold">KRS:</span> 0000437387
                  </p>
                  <button
                    onClick={() => copyToClipboard('0000437387', 'krs')}
                    className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                    title="Skopiuj KRS"
                    aria-label="Skopiuj KRS"
                  >
                    {copiedField === 'krs' ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />}
                  </button>
                </div>
                <div className="flex items-center justify-between bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-white hc-on-white rounded p-3">
                  <p className="body-medium no-margin">
                    <span className="font-semibold">NIP:</span> 1132860384
                  </p>
                  <button
                    onClick={() => copyToClipboard('1132860384', 'nip')}
                    className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                    title="Skopiuj NIP"
                    aria-label="Skopiuj NIP"
                  >
                    {copiedField === 'nip' ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />}
                  </button>
                </div>
                <div className="flex items-center justify-between bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-white hc-on-white rounded p-3">
                  <p className="body-medium no-margin">
                    <span className="font-semibold">REGON:</span> 14639078100000
                  </p>
                  <button
                    onClick={() => copyToClipboard('14639078100000', 'regon')}
                    className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                    title="Skopiuj REGON"
                    aria-label="Skopiuj REGON"
                  >
                    {copiedField === 'regon' ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />}
                  </button>
                </div>
              </div>
            </div>
            <div>
              <h3 className="heading-6 mb-2">
                Dane do przelewu krajowego
              </h3>
              <div className="space-y-3">
                <p className="body-medium">
                  Bank PEKAO S.A.
                </p>
                <div className="flex items-center justify-between bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-white hc-on-white rounded p-3">
                  <p className="heading-6 no-margin font-mono">
                    91 1240 1082 1111 0010 4867 8484
                  </p>
                  <button
                    onClick={() => copyToClipboard('91124010821111001048678484', 'account')}
                    className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                    title="Skopiuj numer konta"
                    aria-label="Skopiuj numer konta"
                  >
                    {copiedField === 'account' ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />}
                  </button>
                </div>
              </div>
            </div>
            <div>
              <h3 className="heading-6 mb-2">
                Dane do przelewu zagranicznego
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-white hc-on-white rounded p-3">
                  <p className="heading-6 no-margin font-mono">
                    IBAN: PL91 1240 1082 1111 0010 4867 8484
                  </p>
                  <button
                    onClick={() => copyToClipboard('PL91124010821111001048678484', 'iban')}
                    className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                    title="Skopiuj IBAN"
                    aria-label="Skopiuj IBAN"
                  >
                    {copiedField === 'iban' ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />}
                  </button>
                </div>
                <div className="flex items-center justify-between bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-white hc-on-white rounded p-3">
                  <p className="heading-6 no-margin font-mono">
                    BIC: PKOPPLPW
                  </p>
                  <button
                    onClick={() => copyToClipboard('PKOPPLPW', 'bic')}
                    className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                    title="Skopiuj BIC"
                    aria-label="Skopiuj BIC"
                  >
                    {copiedField === 'bic' ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />}
                  </button>
                </div>
              </div>
            </div>
            <div>
              <h3 className="heading-6 mb-2">
                PayPal
              </h3>
              <div className="flex items-center justify-between bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-white hc-on-white rounded p-3">
                <a 
                  href="mailto:przytulisko.jb@o2.pl" 
                  className="body-medium hover:text-accessible-red-600 no-margin"
                >
                  przytulisko.jb@o2.pl
                </a>
                <button
                  onClick={() => copyToClipboard('przytulisko.jb@o2.pl', 'paypal')}
                  className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                  title="Skopiuj PayPal"
                  aria-label="Skopiuj PayPal"
                >
                  {copiedField === 'paypal' ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Kontakt: formularz tymczasowo wyłączony */}
      <div className="mt-16 bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
        <h2 className="heading-3 mb-2">Napisz do nas</h2>
        {!ENABLE_CONTACT_FORM ? (
          <p className="body-medium">
            Formularz online będzie dostępny wkrótce. Do tego czasu zapraszamy do kontaktu:
            <br />- telefonicznie: <a href="tel:+48664821160" className="link-primary">664 821 160</a>
            <br />- mailowo: <a href="mailto:znajdki2012@gmail.com" className="link-primary">znajdki2012@gmail.com</a>
          </p>
        ) : (
          <>
            {step === 'edit' && (
              <form
                onSubmit={(e) => { e.preventDefault(); setStep('review'); }}
                noValidate
                aria-describedby="contact-help"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label" htmlFor="name">Imię i nazwisko</label>
                    <input id="name" className="form-input w-full px-4 py-3 rounded-lg border border-accessible-gray-300 dark:border-accessible-gray-700 bg-white dark:bg-accessible-gray-700" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                  </div>
                  <div>
                    <label className="form-label" htmlFor="email">Email</label>
                    <input id="email" type="email" className="form-input w-full px-4 py-3 rounded-lg border border-accessible-gray-300 dark:border-accessible-gray-700 bg-white dark:bg-accessible-gray-700" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                  </div>
                </div>
                <div className="mt-6">
                  <label className="form-label" htmlFor="message">Wiadomość</label>
                  <textarea id="message" className="form-input w-full px-4 py-3 rounded-lg border border-accessible-gray-300 dark:border-accessible-gray-700 bg-white dark:bg-accessible-gray-700" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required />
                  <p id="contact-help" className="form-helper">Krok 1 z 2: wypełnij formularz, potem sprawdzisz dane przed wysłaniem.</p>
                </div>
                <div className="mt-6 flex gap-3">
                  <button type="submit" className="btn-text-medium bg-accessible-red-600 text-white px-6 py-3 rounded-lg">Przejdź do podsumowania</button>
                  <a href="mailto:znajdki2012@gmail.com" className="btn-text-medium px-6 py-3 rounded-lg border border-accessible-gray-300 dark:border-accessible-gray-700">Wolę napisać maila</a>
                </div>
              </form>
            )}
            {step === 'review' && (
              <div>
                <p className="body-medium mb-4">Krok 2 z 2: Sprawdź dane. Jeśli wszystko się zgadza, wyślij. Możesz też wrócić do edycji.</p>
                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-accessible-gray-50 dark:bg-accessible-gray-700 rounded p-3"><span className="label-medium">Imię i nazwisko</span><span className="body-medium">{form.name || '—'}</span></div>
                  <div className="flex items-center justify-between bg-accessible-gray-50 dark:bg-accessible-gray-700 rounded p-3"><span className="label-medium">Email</span><span className="body-medium">{form.email || '—'}</span></div>
                  <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 rounded p-3"><span className="label-medium block mb-2">Wiadomość</span><p className="body-medium no-margin whitespace-pre-line">{form.message || '—'}</p></div>
                </div>
                <div className="mt-6 flex gap-3">
                  <button onClick={() => setStep('edit')} className="btn-text-medium px-6 py-3 rounded-lg border border-accessible-gray-300 dark:border-accessible-gray-700">Wróć do edycji</button>
                  <button onClick={() => setStep('sent')} className="btn-text-medium bg-accessible-red-600 text-white px-6 py-3 rounded-lg">Wyślij</button>
                </div>
              </div>
            )}
            {step === 'sent' && (
              <div role="status" aria-live="polite">
                <p className="heading-5">Dziękujemy! Twoja wiadomość została wysłana.</p>
                <p className="body-medium">Odezwiemy się tak szybko, jak to możliwe.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Mapa */}
      <div className="rounded-lg overflow-hidden shadow-lg">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2618.387636967262!2d21.388153983404614!3d52.42915873724314!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x471edc33cfa829c7%3A0x229340b8f7155607!2sKryszta%C5%82owa%20171%2C%2005-240%20Krusze!5e1!3m2!1sen!2spl!4v1757274831017!5m2!1sen!2spl"
          width="100%"
          height="450"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Lokalizacja Fundacji Znajdki"
          className="w-full"
        ></iframe>
      </div>
    </div>
  );
}